import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SkeletonCard } from "@/components/Skeleton";
import { formatCHF } from "@/lib/orderStatus";
import { TrendingUp, PackageOpen, UserPlus, Award, MoonStar, ArrowRight } from "lucide-react";

function KpiCard({ title, icon: Icon, children, to, testId, accent = false }) {
    const Wrapper = to ? Link : "div";
    const wrapperProps = to ? { to, "data-testid": testId } : { "data-testid": testId };
    return (
        <Wrapper
            {...wrapperProps}
            className={`bc-kpi-card ${to ? "bc-kpi-card--clickable" : ""} ${accent ? "bc-kpi-card--accent" : ""}`}
        >
            <div className="bc-kpi-card__head">
                <span className="bc-kpi-card__title">{title}</span>
                {Icon && <Icon size={16} className="bc-kpi-card__icon" />}
            </div>
            <div className="bc-kpi-card__body">{children}</div>
            {to && (
                <div className="bc-kpi-card__cta">
                    Voir <ArrowRight size={12} />
                </div>
            )}
        </Wrapper>
    );
}

function formatSwissDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return iso.slice(0, 10);
    }
}

export default function AdminDashboard() {
    const { data, isLoading } = useQuery({
        queryKey: ["admin-dashboard"],
        queryFn: async () => (await api.get("/admin/dashboard")).data,
        refetchInterval: 60_000,
    });

    if (isLoading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-dashboard-loading">
                {[0, 1, 2, 3, 4].map((i) => (
                    <SkeletonCard key={i} lines={i > 2 ? 5 : 2} className={i > 2 ? "md:col-span-2" : ""} />
                ))}
            </div>
        );
    }

    const d = data || {};
    const revenue = d.revenue || {};
    const topItems = d.top_items_month || [];
    const dormants = d.dormant_clients || [];
    const maxTop = topItems.reduce((m, x) => Math.max(m, x.quantity_kg || 0), 0) || 1;

    return (
        <div data-testid="admin-dashboard" className="bc-anim-in">
            <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-serif text-2xl text-[#D4BC7C]">Tableau de bord</h2>
                {d.updated_at && (
                    <p className="text-[10px] uppercase tracking-widest text-[#7a7a7a]" data-testid="dashboard-updated-at">
                        Mis à jour à {new Date(d.updated_at).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <KpiCard title="Chiffre d&apos;affaires" icon={TrendingUp} testId="kpi-revenue" accent>
                    <p className="bc-kpi-card__big" data-testid="kpi-revenue-month">
                        {formatCHF(revenue.month || 0)}
                    </p>
                    <p className="bc-kpi-card__hint">ce mois (HT)</p>
                    <div className="bc-kpi-card__sub">
                        <div>
                            <span className="bc-kpi-card__sublabel">Aujourd&apos;hui</span>
                            <span className="bc-kpi-card__subvalue">{formatCHF(revenue.today || 0)}</span>
                        </div>
                        <div>
                            <span className="bc-kpi-card__sublabel">7 derniers jours</span>
                            <span className="bc-kpi-card__subvalue">{formatCHF(revenue.week || 0)}</span>
                        </div>
                    </div>
                </KpiCard>

                <KpiCard
                    title="Commandes à traiter"
                    icon={PackageOpen}
                    to="/admin/commandes"
                    testId="kpi-orders-pending"
                >
                    <p className="bc-kpi-card__big">{d.orders_pending ?? 0}</p>
                    <p className="bc-kpi-card__hint">
                        {(d.orders_pending ?? 0) > 1 ? "commandes en cours" : "commande en cours"}
                    </p>
                </KpiCard>

                <KpiCard
                    title="Comptes à valider"
                    icon={UserPlus}
                    to="/admin/demandes"
                    testId="kpi-users-pending"
                >
                    <p className="bc-kpi-card__big">{d.users_pending ?? 0}</p>
                    <p className="bc-kpi-card__hint">
                        {(d.users_pending ?? 0) > 1 ? "demandes" : "demande"} en attente
                    </p>
                </KpiCard>

                {/* Top 5 articles */}
                <div className="bc-kpi-card md:col-span-2" data-testid="kpi-top-items">
                    <div className="bc-kpi-card__head">
                        <span className="bc-kpi-card__title">Top 5 articles du mois</span>
                        <Award size={16} className="bc-kpi-card__icon" />
                    </div>
                    {topItems.length === 0 ? (
                        <p className="text-sm text-[#A0A0A0] mt-4">Aucune vente ce mois pour l&apos;instant.</p>
                    ) : (
                        <ul className="mt-4 space-y-3">
                            {topItems.map((it, idx) => {
                                const pct = ((it.quantity_kg / maxTop) * 100).toFixed(0);
                                return (
                                    <li key={idx} className="bc-top-item" data-testid={`top-item-${idx}`}>
                                        <div className="bc-top-item__label">
                                            <span className="bc-top-item__rank">{idx + 1}.</span>
                                            <span className="bc-top-item__name">{it.article}</span>
                                            <span className="bc-top-item__code">{it.code}</span>
                                        </div>
                                        <div className="bc-top-item__bar-wrap">
                                            <div className="bc-top-item__bar" style={{ width: `${pct}%` }} />
                                        </div>
                                        <div className="bc-top-item__value">
                                            {it.quantity_kg.toFixed(1).replace(".0", "")} kg
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Clients dormants */}
                <div className="bc-kpi-card" data-testid="kpi-dormant">
                    <div className="bc-kpi-card__head">
                        <span className="bc-kpi-card__title">Clients dormants</span>
                        <MoonStar size={16} className="bc-kpi-card__icon" />
                    </div>
                    {dormants.length === 0 ? (
                        <p className="text-sm text-[#F2ECE0]/80 mt-4">
                            <span className="text-[#D4BC7C]">✨</span> Aucun client dormant, tout roule !
                        </p>
                    ) : (
                        <>
                            <p className="bc-kpi-card__big">{dormants.length}</p>
                            <p className="bc-kpi-card__hint">à relancer (&gt;6 semaines)</p>
                            <ul className="mt-3 space-y-1 text-xs">
                                {dormants.slice(0, 4).map((c) => (
                                    <li key={c.id} className="text-[#F2ECE0]/80 flex justify-between gap-2" data-testid={`dormant-${c.id}`}>
                                        <span className="truncate">{c.raison_sociale}</span>
                                        <span className="text-[#7a7a7a] text-[10px] whitespace-nowrap">
                                            {c.days_since ? `${c.days_since}j` : formatSwissDate(c.last_order_date)}
                                        </span>
                                    </li>
                                ))}
                                {dormants.length > 4 && (
                                    <li className="text-[#7a7a7a] text-[10px] pt-1">+ {dormants.length - 4} autre{dormants.length - 4 > 1 ? "s" : ""}</li>
                                )}
                            </ul>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
